import React, { useEffect, useState, useRef } from 'react';
import DataTable from 'react-data-table-component';
import {
    GET_UPI_ATM_ADMIN_REPORT,
    UPI_ATM_CHECK_STATUS,
    FING_UPI_CW_CHECK_STATUS
} from './../../../Utils/appConstants';
import { getRequest, postRequest } from './../../../Utils/AppApi';
import { useHistory } from "react-router-dom";
import { DatePicker, Tooltip, Select, Space, Tag, Button } from "antd";
import {
    ReloadOutlined,
    PrinterOutlined,
    SearchOutlined,
    ClearOutlined,
    DownloadOutlined,
    FilterOutlined,
} from '@ant-design/icons';
import { getUTCToLocalDateTimeFormat, toDayDate, queryDate } from '../../../Utils/function';
import { CSVLink } from 'react-csv';
import { toast } from 'react-toastify';
import PropagateLoader from "react-spinners/PropagateLoader";
import moment from 'moment';

/* ============================================================
   PERSISTENCE HELPERS
============================================================ */
const FILTERS_STORAGE_KEY = 'upiAtmReportFilters';
const RESTORE_FLAG_KEY = 'upiAtmReportRestore';

const consumeRestoreFlag = () => {
    try {
        const flag = sessionStorage.getItem(RESTORE_FLAG_KEY);
        if (flag === '1') {
            sessionStorage.removeItem(RESTORE_FLAG_KEY);
            return true;
        }
    } catch { /* ignore */ }
    return false;
};

const loadSavedFilters = () => {
    if (!consumeRestoreFlag()) {
        try { sessionStorage.removeItem(FILTERS_STORAGE_KEY); } catch { /* ignore */ }
        return {};
    }
    try {
        const raw = sessionStorage.getItem(FILTERS_STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
};

const restoreDate = (str) => {
    if (!str) return toDayDate();
    const m = moment(str, 'DD-MM-YYYY');
    return m.isValid() ? m : toDayDate();
};

/* ============================================================
   MODERN STYLES (scoped — mirrors AePS CW)
============================================================ */
const reportStyles = `
.aeps-page {
    padding: 16px 24px 40px;
    background: #f5f7fb;
    min-height: calc(100vh - 60px);
}
.aeps-page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 18px;
    flex-wrap: wrap;
    gap: 12px;
}
.aeps-page-title {
    font-size: 22px;
    font-weight: 700;
    color: #1f2a44;
    margin: 0;
    letter-spacing: 0.2px;
}
.aeps-page-title span { color: #e5484d; }
.aeps-page-subtitle {
    font-size: 13px;
    color: #6b7280;
    margin: 2px 0 0;
}

.aeps-card {
    background: #ffffff;
    border-radius: 14px;
    box-shadow: 0 1px 2px rgba(16,24,40,.04), 0 4px 16px rgba(16,24,40,.06);
    border: 1px solid #eef0f4;
    overflow: hidden;
}
.aeps-card + .aeps-card { margin-top: 18px; }

.aeps-card-head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 20px;
    border-bottom: 1px solid #f0f2f6;
    background: linear-gradient(180deg, #fbfcfe 0%, #ffffff 100%);
}
.aeps-card-head-title {
    font-size: 13px;
    font-weight: 600;
    color: #344054;
    text-transform: uppercase;
    letter-spacing: .6px;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
}
.aeps-card-head-title .anticon {
    color: #C9262C;
    font-size: 14px;
}

/* ── Filter grid: 6 columns → row 1 = fields, row 2 = actions ── */
.aeps-filter-body { padding: 18px 20px 20px; }

.aeps-filter-grid {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 14px 16px;
    align-items: end;
}

.aeps-field { display: flex; flex-direction: column; min-width: 0; }
.aeps-field label {
    font-size: 11.5px;
    font-weight: 600;
    color: #667085;
    text-transform: uppercase;
    letter-spacing: .4px;
    margin-bottom: 6px;
}

.aeps-field .ant-picker,
.aeps-field .ant-select,
.aeps-field .aeps-input {
    width: 100%;
    height: 40px;
    border-radius: 10px;
    border: 1px solid #e4e7ec;
    background: #fff;
    transition: border-color .15s, box-shadow .15s;
    font-size: 13.5px;
}
.aeps-field .ant-picker { padding: 0 11px; }
.aeps-field .ant-picker input { font-size: 13.5px; }
.aeps-field .ant-select .ant-select-selector {
    height: 40px !important;
    border-radius: 10px !important;
    border: 1px solid #e4e7ec !important;
    padding: 0 11px !important;
}
.aeps-field .ant-select-single .ant-select-selector .ant-select-selection-item,
.aeps-field .ant-select-single .ant-select-selector .ant-select-selection-placeholder {
    line-height: 38px !important;
    font-size: 13.5px;
}
.aeps-field .ant-picker:hover,
.aeps-field .ant-select:hover .ant-select-selector,
.aeps-field .aeps-input:hover { border-color: #c7cbd3 !important; }
.aeps-field .ant-picker-focused,
.aeps-field .ant-select-focused .ant-select-selector,
.aeps-field .aeps-input:focus {
    border-color: #C9262C !important;
    box-shadow: 0 0 0 3px rgba(201,38,44,.12) !important;
    outline: none;
}

.aeps-input {
    padding: 0 12px;
    color: #101828;
    outline: none;
}
.aeps-input::placeholder { color: #98a2b3; }

/* ══════════════════════════════════════════════════════════════
   ROW 2 — Status on the LEFT, action buttons on the RIGHT,
   with a growing spacer in between.
   Default flex-start keeps Status pinned to the parent's left edge.
   naturally pushes the buttons to the right edge.
   ══════════════════════════════════════════════════════════════ */
.aeps-filter-grid .aeps-actions {
    grid-column: 1 / -1;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 10px;
    min-height: 40px;
    /* no justify-content needed — default flex-start */
}

/* Fixed-width Status dropdown, left-anchored */
.aeps-filter-grid .aeps-actions .aeps-actions-status {
    width: 260px;
    min-width: 220px;
}

/* ⭐ Growing spacer — pushes the buttons to the right */
.aeps-actions-spacer {
    flex: 1 1 auto;         /* consumes all remaining horizontal space */
    min-width: 20px;        /* never collapses to zero */
    height: 40px;           /* aligns with inputs/buttons */
    pointer-events: none;   /* pure layout element */
}

/* ── Buttons ─────────────────────────────────────────── */
.aeps-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 0 20px;
    border-radius: 10px;
    border: 1px solid transparent;
    font-size: 13.5px;
    font-weight: 600;
    cursor: pointer;
    transition: transform .08s, box-shadow .15s, background .15s, border-color .15s;
    white-space: nowrap;
}
.aeps-btn:active { transform: translateY(1px); }

.aeps-btn-primary {
    background: linear-gradient(180deg, #d94348 0%, #C9262C 100%);
    color: #fff;
    box-shadow: 0 2px 6px rgba(201,38,44,.35);
}
.aeps-btn-primary:hover { box-shadow: 0 4px 12px rgba(201,38,44,.45); }

.aeps-btn-ghost {
    background: #ffffff;
    color: #475467;
    border-color: #e4e7ec;
}
.aeps-btn-ghost:hover { background: #f9fafb; border-color: #cfd4dc; }

.aeps-btn-success {
    background: #ffffff;
    color: #0e9384;
    border-color: #b7e4dd;
}
.aeps-btn-success:hover { background: #f0fdfa; border-color: #0e9384; }

/* ── Table ───────────────────────────────────────────── */
.aeps-table-wrap { padding: 4px 6px 10px; }
.aeps-table-wrap .rdt_TableHeadRow {
    background: #f9fafb !important;
    border-bottom: 1px solid #eef0f4 !important;
    min-height: 46px !important;
}
.aeps-table-wrap .rdt_TableCol {
    font-size: 11px !important;
    font-weight: 700 !important;
    color: #475467 !important;
    text-transform: uppercase !important;
    letter-spacing: .4px !important;
}
.aeps-table-wrap .rdt_TableRow {
    border-bottom: 1px solid #f2f4f7 !important;
    min-height: 52px !important;
    transition: background .15s;
}
.aeps-table-wrap .rdt_TableRow:hover { background: #fafbff !important; }
.aeps-table-wrap .rdt_TableCell { font-size: 12.5px !important; color: #344054 !important; }
.aeps-table-wrap .rdt_Pagination {
    border-top: 1px solid #eef0f4 !important;
    background: #fff !important;
    min-height: 52px !important;
}

/* ── Spinner for Inquiry icon ────────────────────────── */
@keyframes aepsSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.aeps-spin { animation: aepsSpin 1s linear infinite; }

/* ── Responsive ──────────────────────────────────────── */
@media (max-width: 1023px) {
    .aeps-filter-grid {
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    }
    .aeps-filter-grid .aeps-actions {
        grid-column: 1 / -1;
        margin-top: 6px;
        padding-top: 16px;
        border-top: 1px dashed #eef0f4;
    }
    /* Status takes its own row, buttons wrap below, spacer unnecessary */
    .aeps-filter-grid .aeps-actions .aeps-actions-status {
        width: 100%;
        min-width: 0;
    }
    .aeps-actions-spacer { display: none; }
}

@media (max-width: 640px) {
    .aeps-page { padding: 12px; }
    .aeps-filter-grid .aeps-actions { justify-content: center; }
    .aeps-filter-grid .aeps-actions .aeps-btn {
        flex: 1 1 auto;
        justify-content: center;
    }
}
`;

/* ============================================================ */

function UpiAtmAdminReport(props) {

    const history = useHistory();
    const csvLink = useRef();
    const limit = 10;
    const dateFormat = 'DD-MM-YYYY';

    const [saved] = useState(() => loadSavedFilters());

    const [searchUserId, setSearchUserId] = useState(saved.searchUserId ?? null);
    const [searchTransId, setSearchTransId] = useState(saved.searchTransId ?? null);
    const [searchMobile, setSearchMobile] = useState(saved.searchMobile ?? null);
    const [searchStatus, setSearchStatus] = useState(saved.searchStatus ?? null);
    const [endDate, setEndDate] = useState(restoreDate(saved.endDate));
    const [startDate, setStartDate] = useState(restoreDate(saved.startDate));

    const [getData, setData] = useState([]);
    const [getCsvData, setCsvData] = useState([]);
    const [getTotalRecord, setTotalRecord] = useState(0);
    const [page, setPage] = useState(saved.page || 1);
    const [isLimit, setLimit] = useState(saved.isLimit || limit);
    const [isLoading, setLoading] = useState(false);
    const [checkingStatus, setCheckingStatus] = useState({});

    const statusOptions = [
        { value: 'SUCCESS', label: 'Success' },
        { value: 'FAILED', label: 'Failed' },
        { value: 'INITIATED', label: 'Initiated' },
        { value: 'PENDING', label: 'Pending' },
        { value: 'EXPIRED', label: 'Expired' }
    ];

    // ============================================================
    // PROVIDER HELPERS
    // ============================================================
    const isFingPayRow = (row) => {
        if (!row) return false;
        if (row.provider === 'FINGPAY') return true;
        const pr = row.partnerRequestId || row.externalRef || '';
        if (String(pr).startsWith('SQ')) return true;
        const ip = row.ipayId || '';
        if (String(ip).startsWith('FING-')) return true;
        return false;
    };

    const getRowKey = (row) => {
        if (!row) return '';
        if (isFingPayRow(row)) {
            return row.partnerRequestId || row.externalRef || row.ipayId || '';
        }
        return row.ipayId || row.externalRef || '';
    };

    const maskVpa = (vpa) => {
        if (!vpa || !vpa.includes('@')) return vpa || '-';
        const atIndex = vpa.indexOf('@');
        const name = vpa.substring(0, atIndex);
        const handle = vpa.substring(atIndex);

        let maskedName;
        if (name.length <= 2) {
            maskedName = name + '***';
        } else if (name.length <= 4) {
            maskedName = name.substring(0, 2) + '***';
        } else {
            maskedName = name.substring(0, 2) + '***' + name.substring(name.length - 1);
        }

        return maskedName + handle;
    };

    // ============================================================
    // CHECK STATUS
    // ============================================================
    const handleCheckStatus = async (record) => {
        const fingpay = isFingPayRow(record);
        const rowKey = getRowKey(record);

        if (!rowKey) {
            toast.error('Transaction ID missing on this row');
            return;
        }

        setCheckingStatus(prev => ({ ...prev, [rowKey]: true }));

        try {
            if (fingpay) {
                const partnerRequestId =
                    record.partnerRequestId || record.externalRef ||
                    String(record.ipayId || '').replace(/^FING-/, '');

                const payload = { partnerRequestId, devicetype: 'W' };
                const response = await postRequest(FING_UPI_CW_CHECK_STATUS, payload, history);

                if (response.success && response.data) {
                    const d = response.data;
                    const newStatus = (d.status || d.qrStatus || '').toUpperCase();

                    setData(prevData =>
                        prevData.map(item => {
                            const sameRow =
                                item.partnerRequestId === partnerRequestId ||
                                item.externalRef === partnerRequestId ||
                                item.ipayId === record.ipayId;
                            if (!sameRow) return item;

                            return {
                                ...item,
                                status: newStatus,
                                qrStatus: newStatus,
                                qrStatusMsg: d.qrStatusMsg || d.responseMessage || 'Status updated',
                                fingpayTxnId: d.fingpayTxnId || item.fingpayTxnId,
                                bankRrn: d.bankRrn || item.bankRrn,
                                operatorId: d.bankRrn || item.operatorId,
                                payerName: d.payer?.name || item.payerName,
                                payerVpa: d.payer?.vpa || item.payerVpa,
                                payerAccountNumber: d.payer?.maskedAccount || item.payerAccountNumber,
                                openingBalance: d.openingBalance ?? item.openingBalance,
                                closingBalance: d.closingBalance ?? item.closingBalance,
                                responseCode: d.responseCode || item.responseCode,
                                responseMessage: d.responseMessage || item.responseMessage,
                            };
                        })
                    );

                    if (newStatus === 'SUCCESS') toast.success('Transaction completed!');
                    else if (newStatus === 'FAILED') toast.error('Transaction failed.');
                    else if (newStatus === 'EXPIRED') toast.warning('QR expired.');
                    else if (newStatus === 'PENDING') toast.info('Payment pending — ask the customer to pay.');
                    else toast.info(`Status: ${newStatus || 'unknown'}`);
                } else {
                    toast.error(response.message || 'Failed to check status');
                }

            } else {
                const payload = { ipayId: record.ipayId, devicetype: 'W' };
                const response = await postRequest(UPI_ATM_CHECK_STATUS, payload, history);

                if (response.success && response.data) {
                    const newStatus = response.data.qrStatus;

                    setData(prevData =>
                        prevData.map(item =>
                            item.ipayId === record.ipayId
                                ? {
                                    ...item,
                                    status: newStatus,
                                    qrStatus: newStatus,
                                    qrStatusMsg: response.data.qrStatusMsg || 'Status updated',
                                    payerName: response.data.payer?.name || item.payerName,
                                    payerVpa: response.data.payer?.vpa || item.payerVpa,
                                    payerMobile: response.data.payer?.mobile || item.payerMobile,
                                    payerAccountNumber: response.data.payer?.maskedAccount || item.payerAccountNumber,
                                    payerBankName: response.data.payer?.bankName || item.payerBankName,
                                    operatorId: response.data.operatorId || item.operatorId,
                                    openingBalance: response.data.openingBalance || item.openingBalance,
                                    closingBalance: response.data.closingBalance || item.closingBalance,
                                    walletIpayId: response.data.walletIpayId || item.walletIpayId,
                                } : item
                        )
                    );

                    if (newStatus === 'SUCCESS') toast.success('Transaction completed!');
                    else if (newStatus === 'FAILED') toast.error('Transaction failed.');
                    else if (newStatus === 'EXPIRED') toast.warning('QR expired.');
                    else toast.info(`Status: ${newStatus}`);
                } else {
                    toast.error(response.message || 'Failed to check status');
                }
            }

        } catch (err) {
            toast.error(err.message || 'Failed to check status');
        } finally {
            setCheckingStatus(prev => ({ ...prev, [rowKey]: false }));
        }
    };

    // ============================================================
    // COLUMNS
    // ============================================================
    const columns = [
        {
            name: 'Date Time',
            cell: row => getUTCToLocalDateTimeFormat(row.createdAt),
            width: "150px",
        },
        {
            name: 'Transaction ID',
            cell: row => {
                const display = isFingPayRow(row)
                    ? (row.partnerRequestId || row.externalRef || row.ipayId)
                    : (row.externalRef || row.ipayId);
                return (
                    <Tooltip placement="bottom" title={display}>
                        <span style={{ fontSize: '12px' }}>{display || '-'}</span>
                    </Tooltip>
                );
            },
            width: "155px",
        },
        {
            name: 'Provider',
            cell: row => (
                <Tag color={isFingPayRow(row) ? 'blue' : 'green'} style={{ fontSize: '10px' }}>
                    {isFingPayRow(row) ? 'FINGPAY' : 'INSTANTPAY'}
                </Tag>
            ),
            width: "100px",
            center: true,
        },
        {
            name: 'Username',
            cell: row => row.userData?.username || '-',
            width: "110px",
        },
        {
            name: 'Partner Name',
            cell: row => row.userData?.name || '-',
            width: "130px",
        },
        {
            name: 'Outlet ID',
            selector: row => row.outletId || '-',
            width: "100px",
        },
        {
            name: 'Cust Mobile',
            selector: row => row.customerMobile || '-',
            width: "115px",
        },
        {
            name: 'Customer',
            cell: row => row.payerName || row.customerName || '-',
            width: "125px",
        },
        {
            name: 'Payer VPA',
            cell: row => (
                <Tooltip placement="bottom" title={maskVpa(row.payerVpa)}>
                    <span style={{ fontSize: '11px' }}>{maskVpa(row.payerVpa) || '-'}</span>
                </Tooltip>
            ),
            width: "140px",
        },
        {
            name: 'Account',
            cell: row => (
                <Tooltip placement="bottom" title={row.payerAccountNumber}>
                    <span style={{ fontSize: '11px' }}>{row.payerAccountNumber || '-'}</span>
                </Tooltip>
            ),
            width: "135px",
        },
        {
            name: 'Bank RRN',
            cell: row => {
                const rrn = row.bankRrn || row.operatorId || '-';
                return (
                    <Tooltip placement="bottom" title={rrn}>
                        <span style={{ fontSize: '11px' }}>{rrn}</span>
                    </Tooltip>
                );
            },
            width: "125px",
        },
        {
            name: 'Amount',
            selector: row => `₹${row.amount || 0}`,
            width: "85px",
            sortable: true,
        },
        {
            name: 'Status',
            cell: row => {
                const config = {
                    INITIATED: { color: '#1890ff', text: 'Initiated' },
                    PENDING: { color: '#faad14', text: 'Pending' },
                    SUCCESS: { color: '#52c41a', text: 'Success' },
                    FAILED: { color: '#ff4d4f', text: 'Failed' },
                    EXPIRED: { color: '#faad14', text: 'Expired' }
                };
                const c = config[row.status] || { color: '#d9d9d9', text: row.status || 'Unknown' };
                return <Tag color={c.color} style={{ fontSize: '11px' }}>{c.text}</Tag>;
            },
            width: "95px",
        },
        {
            name: 'Action',
            cell: (record) => {
                const key = getRowKey(record);
                const isBusy = !!checkingStatus[key];
                const canCheck = record.status === 'INITIATED' || record.status === 'PENDING';

                return (
                    <Space size="small">
                        {canCheck && (
                            <Tooltip title="Check Status">
                                <Button
                                    type="primary"
                                    size="small"
                                    icon={
                                        <ReloadOutlined
                                            className={isBusy ? 'aeps-spin' : ''}
                                        />
                                    }
                                    loading={isBusy}
                                    onClick={() => handleCheckStatus(record)}
                                >
                                    Inquiry
                                </Button>
                            </Tooltip>
                        )}
                        <Tooltip title="Print Invoice">
                            <PrinterOutlined
                                className="pointer"
                                style={{ fontSize: "16px", color: "#C9262C" }}
                                onClick={() => {
                                    try { sessionStorage.setItem(RESTORE_FLAG_KEY, '1'); } catch { /* ignore */ }
                                    history.push(`/upi-atm-invoice/${key}`);
                                }}
                            />
                        </Tooltip>
                    </Space>
                );
            },
            width: "100px",
            center: true,
        },
        {
            name: 'Message',
            cell: row => (
                <Tooltip placement="bottom" title={row.qrStatusMsg || row.failureReason}>
                    <span style={{
                        fontSize: '10px',
                        color: row.status === 'FAILED' ? '#ff4d4f'
                            : row.status === 'SUCCESS' ? '#52c41a'
                                : row.status === 'PENDING' ? '#d48806'
                                    : '#666'
                    }}>
                        {row.qrStatusMsg || row.failureReason || '-'}
                    </span>
                </Tooltip>
            ),
            width: "160px",
        },
    ];

    // ============================================================
    // DATA FETCH
    // ============================================================
    const objectToQueryString = (obj) => {
        var str = [];
        for (var p in obj) {
            if (obj.hasOwnProperty(p)) {
                str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
            }
        }
        return str.join("&");
    };

    const getDataReports = (data) => {
        setLoading(true);
        let params = { page: page, limit: isLimit };

        if (data) {
            setLimit(data);
            params.limit = data;
        }
        if (startDate && endDate) {
            params.startDate = startDate ? queryDate(startDate) : "";
            params.endDate = endDate ? queryDate(endDate) : "";
        }
        params.searchUserId = searchUserId || "";
        params.searchTransId = searchTransId || "";
        params.searchMobile = searchMobile || "";
        params.searchStatus = searchStatus || "";

        const queryString = objectToQueryString(params);
        getRequest(`${GET_UPI_ATM_ADMIN_REPORT}?${queryString}`, history).then((response) => {
            if (response.success === true && response.data) {
                setData(response.data.docs || []);
                setTotalRecord(response.data.totalDocs || 0);
                setPage(response.data.page || 1);
            } else {
                toast.error(response.message || 'Failed to fetch record');
                setData([]);
                setTotalRecord(0);
                setPage(1);
            }
            setLoading(false);
        }).catch(function (error) {
            console.log(error);
            setData([]);
            setTotalRecord(0);
            setPage(1);
            setLoading(false);
        });
    } // eslint-disable-line react-hooks/exhaustive-deps

    const getCSVDataList = () => {
        let params = { exportsCsv: true };
        if (startDate && endDate) {
            params.startDate = queryDate(startDate);
            params.endDate = queryDate(endDate);
        }
        params.searchUserId = searchUserId || "";
        params.searchTransId = searchTransId || "";
        params.searchMobile = searchMobile || "";
        params.searchStatus = searchStatus || "";

        const queryString = objectToQueryString(params);
        getRequest(`${GET_UPI_ATM_ADMIN_REPORT}?${queryString}`, history).then((response) => {
            if (response.success === true) {
                let csvdataRecord = [];
                let csvdata = response.data || [];
                csvdata.forEach((e) => {
                    const fingpay = isFingPayRow(e);
                    const txnId = fingpay
                        ? (e.partnerRequestId || e.externalRef || e.ipayId || '')
                        : (e.externalRef || e.ipayId || '');

                    csvdataRecord.push({
                        "Date Time": getUTCToLocalDateTimeFormat(e.createdAt),
                        "Provider": fingpay ? 'FINGPAY' : 'INSTANTPAY',
                        "Transaction ID": txnId,
                        "External Ref": e.externalRef || '',
                        "IPay ID": e.ipayId || '',
                        "FingPay Txn ID": e.fingpayTxnId || '',
                        "Username": e.userData?.username || '',
                        "Partner Name": e.userData?.name || '',
                        "Outlet ID": e.outletId || '',
                        "Customer Mobile": e.customerMobile || '',
                        "Customer Name": e.payerName || e.customerName || '',
                        "Payer VPA": maskVpa(e.payerVpa) || '',
                        "Payer Mobile": e.payerMobile || '',
                        "Account": e.payerAccountNumber || '',
                        "Payer Bank": e.payerBankName || '',
                        "Amount": e.amount || 0,
                        "Payable": e.payableValue || 0,
                        "Commission": ((e.payableValue || 0) - (e.amount || 0)).toFixed(2),
                        "Opening Balance": e.openingBalance || '',
                        "Closing Balance": e.closingBalance || '',
                        "Bank RRN": e.bankRrn || e.operatorId || '',
                        "Wallet Txn": e.walletIpayId || '',
                        "Txn Mode": e.transactionMode || '',
                        "Status": e.status || '',
                        "Step": e.step || '',
                        "Message": e.qrStatusMsg || e.failureReason || '',
                        "Created": getUTCToLocalDateTimeFormat(e.createdAt),
                        "Completed": e.completedAt ? getUTCToLocalDateTimeFormat(e.completedAt) : ''
                    });
                });
                setCsvData(csvdataRecord);
                setTimeout(() => {
                    if (csvLink.current && csvLink.current.link) {
                        csvLink.current.link.click();
                    }
                }, 500);
            }
        }).catch(function (error) {
            console.log(error);
        });
    };

    const handlePageChange = (page) => {
        setPage(page);
    };

    const onChangeStartDate = (date) => setStartDate(date);
    const onChangeEndDate = (date) => setEndDate(date);

    const handleSearch = () => {
        setPage(1);
        getDataReports();
    };

    /* ---------------------------------------------------------
       Clear — reset to null so antd placeholders reappear
    --------------------------------------------------------- */
    const handleClear = () => {
        try {
            sessionStorage.removeItem(FILTERS_STORAGE_KEY);
            sessionStorage.removeItem(RESTORE_FLAG_KEY);
        } catch { /* ignore */ }

        setStartDate(null);
        setEndDate(null);
        setSearchUserId(null);
        setSearchMobile(null);
        setSearchStatus(null);
        setSearchTransId(null);
        setPage(1);
    };

    /* ============================================================
       PERSIST FILTERS
    ============================================================ */
    useEffect(() => {
        const toSave = {
            startDate: startDate ? moment(startDate).format(dateFormat) : null,
            endDate: endDate ? moment(endDate).format(dateFormat) : null,
            searchUserId,
            searchTransId,
            searchMobile,
            searchStatus,
            page,
            isLimit,
        };
        try {
            sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(toSave));
        } catch { /* ignore */ }
    }, [
        startDate, endDate,
        searchUserId, searchTransId, searchMobile, searchStatus,
        page, isLimit,
    ]);
    /* ============================================================ */

    useEffect(() => {
        getDataReports();
    }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

    if (isLoading) {
        return (
            <div className="content text-center" style={{ margin: "250px 0" }}>
                <PropagateLoader color="#C9262C" loading={isLoading} size={25} />
            </div>
        );
    }

    return (
        <div className="aeps-page">
            <style>{reportStyles}</style>

            {/* ─── Page header ─────────────────────────────────── */}
            <div className="aeps-page-header">
                <div>
                    <h1 className="aeps-page-title">
                        UPI ATM <span>Report</span>
                    </h1>
                    <p className="aeps-page-subtitle">
                        Monitor UPI ATM transactions across FingPay &amp; InstantPay
                    </p>
                </div>
            </div>

            {/* ─── Filters card ───────────────────────────────── */}
            <div className="aeps-card">
                <div className="aeps-card-head">
                    <h2 className="aeps-card-head-title">
                        <FilterOutlined /> Filters
                    </h2>
                </div>

                <div className="aeps-filter-body">
                    <div className="aeps-filter-grid">
                        <div className="aeps-field">
                            <label>From Date</label>
                            <DatePicker
                                value={startDate}
                                format={dateFormat}
                                onChange={onChangeStartDate}
                                placeholder="Select from date"
                            />
                        </div>

                        <div className="aeps-field">
                            <label>To Date</label>
                            <DatePicker
                                value={endDate}
                                format={dateFormat}
                                onChange={onChangeEndDate}
                                placeholder="Select to date"
                            />
                        </div>

                        <div className="aeps-field">
                            <label>User ID / Username</label>
                            <input
                                type="text"
                                className="aeps-input"
                                placeholder="Enter User ID or Username"
                                onChange={(e) => setSearchUserId(e.target.value)}
                                value={searchUserId || ''}
                            />
                        </div>

                        <div className="aeps-field">
                            <label>Transaction ID</label>
                            <input
                                type="text"
                                className="aeps-input"
                                placeholder="Enter Trans ID / IPay ID"
                                onChange={(e) => setSearchTransId(e.target.value)}
                                value={searchTransId || ''}
                            />
                        </div>

                        <div className="aeps-field">
                            <label>Mobile Number</label>
                            <input
                                type="text"
                                className="aeps-input"
                                placeholder="Enter Mobile number"
                                onChange={(e) => setSearchMobile(e.target.value)}
                                value={searchMobile || ''}
                            />
                        </div>

                        {/* ════════ Row 2 — Status LEFT · spacer grows · buttons RIGHT ════════ */}
                        <div className="aeps-actions">
                            <div className="aeps-field aeps-actions-status">
                                <label>Status</label>
                                <Select
                                    options={statusOptions}
                                    value={searchStatus || undefined}
                                    className="normal-light"
                                    classNamePrefix="select"
                                    placeholder="Select status"
                                    onChange={(e) => setSearchStatus(e)}
                                    allowClear
                                />
                            </div>

                            {/* ⭐ Growing spacer — pushes buttons to the right edge */}
                            <div className="aeps-actions-spacer" aria-hidden="true" />

                            <button
                                type="button"
                                className="aeps-btn aeps-btn-ghost"
                                onClick={handleClear}
                            >
                                <ClearOutlined /> Clear
                            </button>

                            <button type="button" className="aeps-btn aeps-btn-success" onClick={getCSVDataList}>
                                <DownloadOutlined /> Export CSV
                            </button>

                            <button type="button" className="aeps-btn aeps-btn-primary" onClick={handleSearch}>
                                <SearchOutlined /> Search
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ─── Table card ─────────────────────────────────── */}
            <div className="aeps-card">
                <div className="aeps-table-wrap">
                    <DataTable
                        title=""
                        columns={columns}
                        data={getData}
                        highlightOnHover
                        pagination
                        paginationServer
                        paginationTotalRows={getTotalRecord}
                        paginationDefaultPage={page}
                        paginationPerPage={isLimit}
                        onChangeRowsPerPage={getDataReports}
                        onChangePage={handlePageChange}
                        progressPending={isLoading}
                        progressComponent={
                            <div style={{ padding: '40px' }}>
                                <PropagateLoader color="#C9262C" loading={true} size={15} />
                            </div>
                        }
                        noDataComponent={
                            <div style={{ padding: '40px 20px', color: '#98a2b3', fontSize: 13, textAlign: 'center' }}>
                                No transactions found for the selected filters.
                            </div>
                        }
                        responsive
                    />
                </div>
            </div>

            {/* hidden CSV trigger */}
            <CSVLink
                data={getCsvData}
                filename='upi-atm-report'
                className='hidden'
                ref={csvLink}
                target='_blank'
                style={{ display: 'none' }}
            />
        </div>
    );
}

export default UpiAtmAdminReport;